import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, History, MessageSquarePlus, Maximize2, X, ArrowLeft } from "lucide-react";
import { useAssistantChat } from "@/hooks/useAssistantChat";
import { AssistantChat } from "./AssistantChat";
import { AssistantHistory } from "./AssistantHistory";

const OPEN_KEY = "dv-assistant-open";

/* Bouton flottant + panneau de conversation disponible sur toutes les pages Studio. */
export const AssistantDock = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => sessionStorage.getItem(OPEN_KEY) === "1");
  const [history, setHistory] = useState(false);
  const chat = useAssistantChat();

  useEffect(() => { sessionStorage.setItem(OPEN_KEY, open ? "1" : "0"); }, [open]);
  useEffect(() => { if (!open) return undefined; const esc = (e) => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", esc); return () => window.removeEventListener("keydown", esc); }, [open]);

  if (location.pathname.startsWith("/studio/assistant")) return null;

  const expand = () => { setOpen(false); navigate("/studio/assistant"); };

  return (
    <>
      {!open && (
        <button
          type="button"
          data-testid="assistant-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-[88px] right-4 z-[55] inline-flex h-12 items-center gap-2 rounded-full bg-[#0F2645] py-3 pl-3.5 pr-4 text-sm font-semibold text-white shadow-[0_12px_30px_-10px_rgba(15,38,69,0.6)] transition-transform duration-150 hover:-translate-y-0.5 active:scale-95 lg:bottom-6 lg:right-6"
          aria-label="Ouvrir l'assistant"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#3A5CC8]"><Sparkles className="h-4 w-4" /></span>
          Assistant
        </button>
      )}

      {open && (
        <div data-testid="assistant-dock" className="fixed inset-0 z-[70] flex flex-col bg-white lg:inset-auto lg:bottom-6 lg:right-6 lg:h-[min(720px,calc(100vh-48px))] lg:w-[440px] lg:overflow-hidden lg:rounded-3xl lg:border lg:border-[#E2E7F7] lg:shadow-[0_30px_80px_-20px_rgba(15,38,69,0.45)]" role="dialog" aria-label="Assistant Devisto" style={{ animation: "devFadeUp .18s ease-out" }}>
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-[#E2E7F7] bg-white px-3">
            <div className="flex items-center gap-2">
              {history ? (
                <button type="button" data-testid="assistant-history-back" onClick={() => setHistory(false)} className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[#5C6A87] hover:bg-slate-100"><ArrowLeft className="h-4.5 w-4.5" /></button>
              ) : (
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#3A5CC8] text-white"><Sparkles className="h-4 w-4" /></span>
              )}
              <p className="font-heading text-base font-bold text-[#0F2645]">{history ? "Conversations" : "Assistant"}</p>
            </div>
            <div className="flex items-center gap-0.5 text-[#5C6A87]">
              <button type="button" data-testid="assistant-dock-history" onClick={() => setHistory((h) => !h)} className={`inline-flex h-9 w-9 items-center justify-center rounded-xl hover:bg-slate-100 ${history ? "bg-[#EEF1FC] text-[#3A5CC8]" : ""}`} aria-label="Historique"><History className="h-4.5 w-4.5" /></button>
              <button type="button" data-testid="assistant-dock-new" onClick={async () => { await chat.startNew(); setHistory(false); }} className="inline-flex h-9 w-9 items-center justify-center rounded-xl hover:bg-slate-100" aria-label="Nouvelle conversation"><MessageSquarePlus className="h-4.5 w-4.5" /></button>
              <button type="button" data-testid="assistant-dock-expand" onClick={expand} className="inline-flex h-9 w-9 items-center justify-center rounded-xl hover:bg-slate-100" aria-label="Plein écran"><Maximize2 className="h-4.5 w-4.5" /></button>
              <button type="button" data-testid="assistant-dock-close" onClick={() => setOpen(false)} className="inline-flex h-9 w-9 items-center justify-center rounded-xl hover:bg-slate-100" aria-label="Fermer"><X className="h-4.5 w-4.5" /></button>
            </div>
          </div>
          <div className="min-h-0 flex-1 bg-[#FBFCFE]">
            {history ? <AssistantHistory chat={chat} onPick={() => setHistory(false)} /> : <AssistantChat chat={chat} compact />}
          </div>
        </div>
      )}
    </>
  );
};
